import React, { useState, Suspense, lazy, useEffect, useRef, useLayoutEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Toaster, toast } from 'react-hot-toast';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { ErrorBoundary } from 'react-error-boundary';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { downloadProcessedImage, getFileInfo, estimateOutputSize, convertPdfToImage, processImage } from './utils/imageProcessing';
import { OutputFormat, SelectedSize, ProcessedImage, PrintSize, FileInfo } from './types';
import { ImagePreview } from './components/ImagePreview';
import { useSubscription } from './context/SubscriptionContext';
import { SubscriptionBanner } from './components/SubscriptionBanner';
import { useAuth } from './context/FirebaseAuthContext';
import { useUserCredits } from './hooks/useUserCredits';
import Header from './components/Header';
import LandingContent, { LandingPage } from './components/LandingContent';
import landingPagesData from './content/landingPages.json';
import homePageData from './content/homePage.json';

const LANDING_PAGES = landingPagesData as LandingPage[];
import { GuestSubscriptionModal } from './components/GuestSubscriptionModal';
import { SubscriberLogin } from './components/SubscriberLogin';
import { GuestSession } from './utils/guestSession';
import { createUserProfile } from './services/firebaseService';

// Yeni PDF dönüştürme modülünü import et
import { convertPdfWithFallback } from './utils/pdfConverter';

// Simplify the lazy imports
const ImageUploader = lazy(() => import('./components/ImageUploader'));
const SizeSelector = lazy(() =>
  import('./components/SizeSelector').then(module => ({ default: module.SizeSelector }))
);
const ConversionSettings = lazy(() => import('./components/ConversionSettings'));

// Lazy load pages
const PricingPage = lazy(() => import('./pages/Pricing'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const Contact = lazy(() => import('./pages/Contact'));
const Login = lazy(() => import('./pages/Login'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const AuthConfirm = lazy(() => import('./pages/AuthConfirm'));
const DpiChecker = lazy(() => import('./pages/tools/DpiChecker'));
const PrintSizeChecker = lazy(() => import('./pages/tools/PrintSizeChecker'));
const PixelCalculator = lazy(() => import('./pages/tools/PixelCalculator'));
const ImageResizer = lazy(() => import('./pages/tools/ImageResizer'));
const ToolsIndex = lazy(() => import('./pages/tools/ToolsIndex'));
const CropToPrintSize = lazy(() => import('./pages/tools/CropToPrintSize'));
const PrintableSizePack = lazy(() => import('./pages/tools/PrintableSizePack'));
const BleedTool = lazy(() => import('./pages/tools/BleedTool'));
const KdpCoverCalculator = lazy(() => import('./pages/tools/KdpCoverCalculator'));
const PrintSizeChart = lazy(() => import('./pages/tools/PrintSizeChart'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CreditHistoryPage = lazy(() => import('./pages/CreditHistoryPage'));
const AdminPanel = lazy(() => import('./pages/Admin'));
const About = lazy(() => import('./pages/About'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));

// Modern UI için yeni Hero bileşeni
const HeroSection = React.memo(({ heading, description }: { heading?: React.ReactNode; description?: string }) => {
  const { isSubscribed } = useSubscription();

  return (
    <header className="bg-gradient-to-r from-blue-700 to-indigo-800 py-8 md:py-16 relative overflow-hidden">
      {/* Dekoratif arka plan elementleri */}
      <div className="absolute inset-0 bg-black/30 mix-blend-multiply"></div>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-repeat" style={{
          backgroundImage: 'url("/pattern.svg")',
          backgroundSize: '200px',
        }}></div>
      </div>

      {/* Dekoratif şekiller */}
      <div className="absolute right-0 top-0 -translate-y-1/4 translate-x-1/4 w-64 h-64 bg-gradient-to-br from-blue-400/20 to-indigo-500/20 rounded-full blur-3xl"></div>
      <div className="absolute left-0 bottom-0 translate-y-1/3 -translate-x-1/3 w-96 h-96 bg-gradient-to-tr from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center space-y-5 md:space-y-7">
          {/* Ana başlık - image dpi converter vurgusu */}

          {/* Premium Status Banner */}
          <div className="flex items-center justify-center space-x-4 mb-4">
            {isSubscribed ? (
              <div className="bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-500/30 backdrop-blur-md rounded-full px-5 py-2 text-white flex items-center gap-2 shadow-lg">
                <span className="h-2 w-2 rounded-full bg-yellow-400 animate-ping"></span>
                <span className="text-sm font-bold text-yellow-300">👑 Premium Active (Unlimited Access)</span>
              </div>
            ) : null}
          </div>

          <h1
            className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight text-white"
            id="main-heading"
          >
            {heading || <>Image <span className="text-blue-300">DPI Converter</span></>}
          </h1>

          {/* Alt metin - SEO optimizasyonu korundu */}
          <p
            id="react-hero-text"
            className="text-base md:text-xl text-blue-100/90 leading-relaxed max-w-2xl mx-auto"
          >
            {description || "Easily modify the DPI of any image format. Convert to 72-600 DPI for perfect print quality with our user-friendly tool. Supports JPEG, PNG, GIF and more."}
          </p>

          {/* Modern tasarımlı özellik kartları */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-5 mt-8">
            {[
              {
                title: 'Convert Common Image Formats',
                desc: 'JPG, PNG, GIF and more formats supported',
                icon: '📁'
              },
              {
                title: 'Set Desired DPI',
                desc: 'From 72 DPI to 600 DPI (dots per inch)',
                icon: '🎯'
              },
              {
                title: 'Convert Images in Bulk',
                desc: 'Process multiple files at once',
                icon: '⚡'
              }
            ].map(feature => (
              <div key={feature.title} className="bg-white/10 backdrop-blur-md rounded-xl p-5 text-white border border-white/10 hover:border-blue-400/30 transition-all hover:shadow-lg hover:shadow-blue-500/10">
                <span className="text-2xl mb-3 block">{feature.icon}</span>
                <h2 className="font-medium text-lg text-blue-100">{feature.title}</h2>
                <p className="text-sm text-blue-200/80 mt-2">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
});

// Declare the LCPReporter for TypeScript
interface Window {
  LCPReporter?: (element: HTMLElement) => void;
  dataLayer: any[];
}

// Google Analytics izleme fonksiyonları
const initializeAnalytics = () => {
  // Eğer zaten yüklenmişse tekrar ekleme
  if (window.dataLayer) return;

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer.push(arguments);
  }
  gtag('js', new Date());
  gtag('config', 'G-PE4BX79TLP', { send_page_view: false });
};

const trackEvent = (category: string, action: string, label?: string, value?: number) => {
  if (!window.dataLayer) return;

  window.dataLayer.push({
    event: 'custom_event',
    event_category: category,
    event_action: action,
    event_label: label,
    event_value: value
  });
};

// Scroll to top component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Ana Uygulama Bileşeni
function App() {
  const [selectedSizes, setSelectedSizes] = useState<SelectedSize[]>([]);
  const [processedImages, setProcessedImages] = useState<ProcessedImage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<OutputFormat>('png');
  const [editingImageId, setEditingImageId] = useState<string | null>(null);
  const [baseFileName, setBaseFileName] = useState<string>('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [customDpi, setCustomDpi] = useState(300);

  // Firebase Auth ve Credits
  const { user } = useAuth();
  // Alias for legacy code that uses currentUser
  const currentUser = user;
  const { userData, spendCredits, checkCredits } = useUserCredits();

  // Guest subscription modal state
  const [showGuestSubscriptionModal, setShowGuestSubscriptionModal] = useState(false);

  // Abonelik durumunu al (eski sistem - geçiş dönemi için)
  const {
    isSubscribed,
    freeConversionsUsed,
    freeConversionsLimit,
    hasRemainingFreeConversions,
    incrementFreeConversions
  } = useSubscription();

  const location = useLocation();

  // Dynamic SEO metadata and JSON-LD schemas based on the path for 2026 AI-driven SEO
  const landingPage = LANDING_PAGES.find(p => p.path === location.pathname);

  const getPageMeta = () => {
    if (landingPage) {
      return {
        title: landingPage.title,
        description: landingPage.description,
        heading: <>{landingPage.heading} <span className="text-blue-300">{landingPage.highlight}</span></>,
        descriptionText: landingPage.intro,
        schema: {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": landingPage.title.split(' | ')[0],
          "description": landingPage.description,
          "url": `https://www.imagedpiconverter.com${landingPage.path}`,
          "applicationCategory": "DesignApplication",
          "operatingSystem": "Web Browser",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
        }
      };
    }
    switch (location.pathname) {
      case '/convert-to-300-dpi':
        return {
          title: "Convert Image to 300 DPI Online Free | Image DPI Converter",
          description: "Easily convert your images to 300 DPI online for free. Perfect for professional print quality. Supports JPG, PNG, and PDF formats.",
          heading: <>Convert Image to <span className="text-blue-300">300 DPI</span></>,
          descriptionText: "Change the resolution of your JPEG or PNG images to 300 DPI (dots per inch) for high-quality printing. Our free tool does not upload your files.",
          schema: {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Image DPI Converter - 300 DPI",
            "description": "Convert images to 300 DPI online free. Ideal for photographers, publishers, and graphic designers looking for professional print quality.",
            "url": "https://www.imagedpiconverter.com/convert-to-300-dpi",
            "applicationCategory": "DesignApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          }
        };
      case '/convert-to-600-dpi':
        return {
          title: "Convert Image to 600 DPI Online Free | Image DPI Converter",
          description: "Convert images to 600 DPI online for ultra-high quality printing. Free, fast browser-based tool supporting JPG, PNG, and PDF formats.",
          heading: <>Convert Image to <span className="text-blue-300">600 DPI</span></>,
          descriptionText: "Convert your photos to 600 DPI resolution online. Perfect for high-resolution graphics, art prints, and high-density publications.",
          schema: {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Image DPI Converter - 600 DPI",
            "description": "Convert images to 600 DPI online free. Highly optimized for professional printing and digital graphic restoration.",
            "url": "https://www.imagedpiconverter.com/convert-to-600-dpi",
            "applicationCategory": "DesignApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          }
        };
      case '/bulk-dpi-converter':
        return {
          title: "Bulk Image DPI Converter Online Free | Batch DPI Changer",
          description: "Batch process and convert multiple images to 300 or 600 DPI simultaneously. Free online bulk DPI changer with no file uploads.",
          heading: <>Bulk Image <span className="text-blue-300">DPI Converter</span></>,
          descriptionText: "Convert multiple files at once. Upload a batch of JPG, PNG, or GIF files and apply your preferred DPI settings in seconds.",
          schema: {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Bulk Image DPI Converter",
            "description": "Convert multiple images to 300 or 600 DPI in batch mode. Safe client-side browser processing with zero uploads.",
            "url": "https://www.imagedpiconverter.com/bulk-dpi-converter",
            "applicationCategory": "DesignApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          }
        };
      case '/dpi-calculator':
        return {
          title: "DPI Calculator & Resolution Converter Online | Free Tool",
          description: "Calculate pixel dimensions for your print size at 300 or 600 DPI, and convert your image's DPI instantly. Free online resolution tool.",
          heading: <>DPI <span className="text-blue-300">Calculator & Converter</span></>,
          descriptionText: "Calculate the exact pixels required for your physical print sizes or convert standard image DPI (dots per inch) instantly in your browser.",
          schema: {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "DPI Calculator & Resolution Converter",
            "description": "Calculate pixel dimensions for physical print sizes at custom DPI values and adjust resolution instantly.",
            "url": "https://www.imagedpiconverter.com/dpi-calculator",
            "applicationCategory": "DesignApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          }
        };
      case '/about':
        return {
          title: "About Us | Image DPI Converter",
          description: "Learn about the mission, features, and browser-based technology of Image DPI Converter.",
          heading: <>About <span className="text-blue-300">Us</span></>,
          descriptionText: "Our mission is to provide an accessible, secure, and fast solution for image DPI conversion directly in your browser.",
          schema: {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About Us | Image DPI Converter",
            "description": "Learn about the mission, features, and creators of Image DPI Converter.",
            "url": "https://www.imagedpiconverter.com/about"
          }
        };
      case '/privacy':
        return {
          title: "Privacy Policy | Image DPI Converter",
          description: "Privacy policy and client-side processing terms. We do not upload or store any of your images on our servers.",
          heading: <>Privacy <span className="text-blue-300">Policy</span></>,
          descriptionText: "Read our privacy policy. Your image privacy is fully protected as all operations happen locally.",
          schema: {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Privacy Policy | Image DPI Converter",
            "description": "Privacy policy and browser-based data processing terms for Image DPI Converter.",
            "url": "https://www.imagedpiconverter.com/privacy"
          }
        };
      case '/terms':
        return {
          title: "Terms of Service | Image DPI Converter",
          description: "Terms of service and usage rights for our free browser-based Image DPI Converter tool.",
          heading: <>Terms of <span className="text-blue-300">Service</span></>,
          descriptionText: "Read our terms of service. Understand usage limits and browser-based licensing conditions.",
          schema: {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Terms of Service | Image DPI Converter",
            "description": "Terms of service and usage rights for Image DPI Converter.",
            "url": "https://www.imagedpiconverter.com/terms"
          }
        };
      case '/refund':
        return {
          title: "Refund and Cancellation Policy | Image DPI Converter",
          description: "Refund terms, 7-day trial policy, and cancellation procedures for premium subscriptions.",
          heading: <>Refund <span className="text-blue-300">Policy</span></>,
          descriptionText: "Read our refund and cancellation terms. Learn about trial options and subscription cancellation.",
          schema: {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Refund and Cancellation Policy | Image DPI Converter",
            "description": "Refund and subscription cancellation terms for Image DPI Converter.",
            "url": "https://www.imagedpiconverter.com/refund"
          }
        };
      case '/contact':
        return {
          title: "Contact Us | Image DPI Converter",
          description: "Get in touch with the Image DPI Converter team for support, feature requests, or business inquiries.",
          heading: <>Contact <span className="text-blue-300">Us</span></>,
          descriptionText: "Get support or send feedback. We respond within 24 hours.",
          schema: {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Us | Image DPI Converter",
            "description": "Get in touch with the Image DPI Converter team for support or business inquiries.",
            "url": "https://www.imagedpiconverter.com/contact"
          }
        };
      default:
        return {
          title: "DPI Converter – Free Online Image DPI Converter",
          description: "Free online DPI converter: change JPG, PNG and PDF images to 300 or 600 DPI at any print size. Real pixel resampling, no upload, no sign-up.",
          heading: <>Image <span className="text-blue-300">DPI Converter</span></>,
          descriptionText: "Easily modify the DPI of any image format. Convert to 72-600 DPI for perfect print quality with our user-friendly tool. Supports JPEG, PNG, GIF and more.",
          schema: {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Image DPI Converter",
            "description": "Free online tool to convert image DPI from 72 to 600 DPI. Change photo resolution, enhance image quality, and convert to 300 DPI for print.",
            "url": "https://www.imagedpiconverter.com",
            "applicationCategory": "DesignApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          }
        };
    }
  };

  const pageMeta = getPageMeta();

  // Set default DPI based on path on load
  useEffect(() => {
    if (location.pathname === '/convert-to-300-dpi') {
      setCustomDpi(300);
    } else if (location.pathname === '/convert-to-600-dpi') {
      setCustomDpi(600);
    }
  }, [location.pathname]);

  // Update size estimations when settings change
  useEffect(() => {
    updateSizeEstimates();
  }, [selectedSizes, selectedFormat, customDpi]);

  // Analytics izleme için yeni effect
  useEffect(() => {
    // Google Analytics'i başlat
    initializeAnalytics();

    // Sayfa görüntülemeyi kaydet
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_title: pageMeta.title,
        page_location: window.location.href,
        page_path: location.pathname
      });
    }
  }, [location.pathname, pageMeta.title]);

  // Firebase kullanıcı profili oluşturma
  useEffect(() => {
    const initializeUserProfile = async () => {
      if (currentUser) {
        try {
          await createUserProfile(currentUser);
        } catch (error) {
          console.error('Failed to create user profile:', error);
        }
      }
    };

    initializeUserProfile();
  }, [currentUser]);

  const updateSizeEstimates = () => {
    if (processedImages.length === 0) return;

    const activeSizes = selectedSizes.filter(s => s.isSelected);
    if (activeSizes.length === 0) return;

    setProcessedImages(prevImages =>
      prevImages.map(img => {
        if (!img.dimensions.width) return img;

        const estimatedSizes: Record<string, number> = {};
        activeSizes.forEach(size => {
          const estimate = estimateOutputSize(
            img.size,
            img.dimensions,
            size,
            selectedFormat,
            img.dpi || 72,
            customDpi
          );
          const sizeKey = `${size.name}_${selectedFormat}`;
          estimatedSizes[sizeKey] = estimate;
        });

        return {
          ...img,
          estimatedSizes
        };
      })
    );
  };

  const handleSizeToggle = (size: PrintSize) => {
    setSelectedSizes(prev => {
      const exists = prev.find(s => s.name === size.name);
      if (exists) {
        return prev.map(s =>
          s.name === size.name
            ? { ...s, isSelected: !s.isSelected }
            : s
        );
      }
      return [...prev, { ...size, isSelected: true }];
    });
  };

  const handleFormatChange = (format: string) => {
    // OutputFormat tipine dönüştürme yapıyoruz
    setSelectedFormat(format as OutputFormat);
  };

  const handleImageUpload = async (file: File) => {
    // Check if user is subscribed or has remaining free conversions / credits
    if (!isSubscribed) {
      if (currentUser) {
        // Logged-in user: check credits
        const hasCredits = await checkCredits(1);
        if (!hasCredits && userData && userData.credits < 1) {
          toast.error(
            <div>
              <p>You don't have enough credits. Please purchase more credits or upgrade your plan.</p>
              <div className="mt-2">
                <Link
                  to="/pricing"
                  className="inline-block mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-center hover:bg-blue-700"
                >
                  View Pricing
                </Link>
              </div>
            </div>,
            {
              duration: 6000,
              style: { minWidth: '300px' }
            }
          );
          return;
        }
      } else {
        // Guest user: check remaining free conversions
        if (!hasRemainingFreeConversions) {
          toast.error(
            <div>
              <p className="font-semibold text-white">You have used all your free guest conversions.</p>
              <p className="text-xs text-blue-200 mt-1">Subscribe to Go Premium for unlimited high-quality conversions.</p>
              <div className="mt-2.5">
                <Link
                  to="/pricing"
                  className="inline-block px-4 py-2 bg-white hover:bg-blue-50 text-blue-700 font-bold rounded-lg text-xs text-center shadow-md transition-colors duration-200"
                >
                  Go Premium ⚡
                </Link>
              </div>
            </div>,
            {
              duration: 8000,
              style: { minWidth: '350px' }
            }
          );
          return;
        }
      }
    }

    const baseName = file.name.replace(/\.[^/.]+$/, "");

    toast.loading('Analyzing file...', { id: 'file-analysis' });

    try {
      const fileInfo = await getFileInfo(file);

      let fileToProcess = file;

      // PDF dönüştürme işlemi için yeni modülü kullanıyoruz
      if (file.type === 'application/pdf') {
        const shouldConvert = window.confirm('Do you want to convert this PDF to an image?');
        if (shouldConvert) {
          toast.loading('Converting PDF to image...', { id: 'pdf-conversion' });

          try {
            // Yeni PDF dönüştürme modülünü kullan
            fileToProcess = await convertPdfWithFallback(
              file,
              selectedFormat as 'jpeg' | 'png' || 'png',
              customDpi
            );
            toast.success('PDF converted to image!', { id: 'pdf-conversion' });
          } catch (pdfError) {
            console.error('PDF conversion error:', pdfError);
            toast.error('Failed to convert PDF. Please try again or use a different file.', { id: 'pdf-conversion' });
            // Hata durumunda işlemi iptal et
            toast.dismiss('file-analysis');
            return;
          }
        }
      }

      const newImage: ProcessedImage = {
        id: uuidv4(),
        file: fileToProcess,
        name: baseName,
        originalName: baseName,
        preview: URL.createObjectURL(fileToProcess),
        size: fileToProcess.size,
        dimensions: fileInfo.dimensions,
        dpi: fileInfo.dpi,
        fileType: fileInfo.fileType
      };

      setProcessedImages(prev => [...prev, newImage]);
      toast.success('File uploaded successfully!');

      // Dosya yüklemeyi izle
      trackEvent('File', 'Upload', file.type, Math.round(file.size / 1024));

      // Firebase kredi sistemi - dosya yükleme için kredi harcama (isteğe bağlı)
      // Not: Krediyi download sırasında harcayabiliriz, upload sırasında değil

      // Kullanıcının kalan kredi sayısını göster (premium kullanıcılara gösterme)
      if (!isSubscribed && userData && userData.credits <= 5) {
        toast(
          <div>
            <p>You have {userData.credits} credits remaining. Consider upgrading your plan.</p>
            <div className="mt-2">
              <Link
                to="/pricing"
                className="inline-block mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-center hover:bg-blue-700"
              >
                View Pricing
              </Link>
            </div>
          </div>,
          {
            icon: '⚠️',
            duration: 4000,
            style: { minWidth: '300px' }
          }
        );
      }

      // toast.info yerine toast.success veya toast kullan
      if (selectedSizes.filter(s => s.isSelected).length === 0) {
        toast('Please select print sizes', {
          icon: '📏',
          duration: 4000
        });
      }
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to process file');
    } finally {
      toast.dismiss('file-analysis');
    }
  };

  const getFormattedFileName = (baseName: string, size: PrintSize) => {
    const fileName = baseFileName || baseName;
    return `${fileName}_${size.width}x${size.height}_${customDpi}dpi`;
  };

  const handleBulkDownload = async () => {
    if (processedImages.length === 0) {
      toast.error('No images to process');
      return;
    }

    const activeSizes = selectedSizes.filter(s => s.isSelected);
    if (activeSizes.length === 0) {
      toast.error('Please select at least one size');
      return;
    }

    const totalCreditsNeeded = processedImages.length * activeSizes.length;

    // Check credits or free limits if not premium
    if (!isSubscribed) {
      if (currentUser) {
        const hasEnoughCredits = await checkCredits(totalCreditsNeeded);
        if (!hasEnoughCredits) {
          toast.error(
            <div>
              <p>You need {totalCreditsNeeded} credits but only have {userData?.credits || 0}. Please purchase more credits.</p>
              <div className="mt-2">
                <Link
                  to="/pricing"
                  className="inline-block mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-center hover:bg-blue-700"
                >
                  View Pricing
                </Link>
              </div>
            </div>,
            {
              duration: 6000,
              style: { minWidth: '300px' }
            }
          );
          return;
        }
      } else {
        // Guest user
        const freeConversionsLeft = freeConversionsLimit - freeConversionsUsed;
        if (totalCreditsNeeded > freeConversionsLeft) {
          toast.error(
            <div>
              <p className="font-semibold text-white">You need {totalCreditsNeeded} conversions but only have {freeConversionsLeft} free guest conversions left.</p>
              <p className="text-xs text-blue-200 mt-1">Upgrade to Premium to process multiple images at once.</p>
              <div className="mt-2.5">
                <Link
                  to="/pricing"
                  className="inline-block px-4 py-2 bg-white hover:bg-blue-50 text-blue-700 font-bold rounded-lg text-xs text-center shadow-md transition-colors duration-200"
                >
                  Go Premium ⚡
                </Link>
              </div>
            </div>,
            {
              duration: 8000,
              style: { minWidth: '350px' }
            }
          );
          return;
        }
      }
    }

    setIsProcessing(true);
    const total = processedImages.length * activeSizes.length;
    let processed = 0;

    try {
      const toastId = toast.loading(`Processing 0/${total} images...`);

      // Spend credits / increment free conversions for non-premium users
      if (!isSubscribed) {
        if (currentUser) {
          const creditsSpent = await spendCredits(totalCreditsNeeded, 'bulk_image_conversion');
          if (!creditsSpent) {
            toast.error('Failed to process credits. Please try again.');
            setIsProcessing(false);
            return;
          }
        } else {
          // Guest user: increment free conversions
          for (let i = 0; i < totalCreditsNeeded; i++) {
            incrementFreeConversions();
          }
        }
      }

      for (const image of processedImages) {
        for (const size of activeSizes) {
          try {
            const fileName = getFormattedFileName(image.name, size);

            // Doğrudan client-side işleme kullan
            const blob = await processImage(
              image.file,
              size,
              selectedFormat,
              customDpi
            );

            // İndirme işlemi
            const finalFileName = `${fileName}_${customDpi}dpi.${selectedFormat}`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = finalFileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            processed++;
            toast.loading(`Processing ${processed}/${total} images...`, { id: toastId });
          } catch (error) {
            console.error('Failed to process image:', error);
          }
        }
      }

      toast.success(`Successfully processed ${processed}/${total} images!`, { id: toastId });

      // İndirme işlemini izle
      trackEvent('Conversion', 'Bulk Download', selectedFormat, processedImages.length);

      // Kalan kredi uyarısı (premium kullanıcılara gösterme)
      if (!isSubscribed && userData && userData.credits - totalCreditsNeeded <= 3) {
        toast(
          <div>
            <p>You have {userData.credits - totalCreditsNeeded} credits remaining. Consider upgrading your plan.</p>
            <div className="mt-2">
              <Link
                to="/pricing"
                className="inline-block mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-center hover:bg-blue-700"
              >
                View Pricing
              </Link>
            </div>
          </div>,
          {
            icon: '⚠️',
            duration: 4000,
            style: { minWidth: '300px' }
          }
        );
      }
    } catch (error) {
      console.error('Bulk download error:', error);
      toast.error('Failed to process some images');
    } finally {
      setIsProcessing(false);
    }
  };

  async function handleDownloadSingle(size: SelectedSize) {
    // Premium users skip all limits; guests use their free conversions; logged-in users use credits
    const hasEnoughCredits = isSubscribed
      ? true
      : currentUser
        ? await checkCredits(1)
        : hasRemainingFreeConversions;
    if (!hasEnoughCredits) {
      if (!currentUser) {
        // Show guest subscription modal for non-authenticated users
        setShowGuestSubscriptionModal(true);
        return;
      } else {
        // Show pricing toast for authenticated users
        toast.error(
          <div>
            <p>You don't have enough credits. Please purchase more credits or upgrade your plan.</p>
            <div className="mt-2">
              <Link
                to="/pricing"
                className="inline-block mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-center hover:bg-blue-700"
              >
                View Pricing
              </Link>
            </div>
          </div>,
          {
            duration: 6000,
            style: { minWidth: '300px' }
          }
        );
        return;
      }
    }

    const imageToProcess = processedImages[0];
    if (!imageToProcess) return;

    setIsProcessing(true);
    try {
      const printSize: PrintSize = {
        name: size.name,
        width: size.width,
        height: size.height,
        category: size.category
      };

      const fileName = getFormattedFileName(imageToProcess.name, printSize);

      // Doğrudan client-side işleme kullan
      const blob = await processImage(
        imageToProcess.file,
        printSize,
        selectedFormat,
        customDpi
      );

      // İndirme işlemi
      const finalFileName = `${fileName}_${customDpi}dpi.${selectedFormat}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = finalFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Firebase/Supabase kredi veya misafir limit sistemi - single download için harca/say
      if (!isSubscribed) {
        if (currentUser) {
          const creditsSpent = await spendCredits(1, 'single_image_conversion');
          if (creditsSpent) {
            toast.success('File downloaded successfully');
            if (userData && userData.credits - 1 <= 3) {
              toast(
                <div>
                  <p>You have {userData.credits - 1} credits remaining. Consider upgrading your plan.</p>
                  <div className="mt-2">
                    <Link
                      to="/pricing"
                      className="inline-block mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-center hover:bg-blue-700"
                    >
                      View Pricing
                    </Link>
                  </div>
                </div>,
                {
                  icon: '⚠️',
                  duration: 4000,
                  style: { minWidth: '300px' }
                }
              );
            }
          } else {
            toast.error('Failed to process credits. Please try again.');
          }
        } else {
          // Guest user: increment free conversions
          incrementFreeConversions();
          toast.success('File downloaded successfully');
          const remaining = freeConversionsLimit - (freeConversionsUsed + 1);
          if (remaining >= 0) {
            toast(`You have ${remaining} free guest conversions remaining.`, {
              icon: 'ℹ️',
              duration: 4500
            });
          }
        }
      } else {
        toast.success('File downloaded successfully');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    } finally {
      setIsProcessing(false);
    }
  }

  const handleRemoveImage = (id: string) => {
    setProcessedImages(prev => prev.filter(img => img.id !== id));
  };

  const handleNameChange = (id: string, newName: string) => {
    setProcessedImages(prev => prev.map(img =>
      img.id === id ? { ...img, name: newName } : img
    ));
    setEditingImageId(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
    else return (bytes / 1048576).toFixed(2) + ' MB';
  };

  const renderImageList = () => (
    <div className="space-y-3">
      {processedImages.map(image => (
        <ImagePreview
          key={image.id}
          image={image}
          selectedSizes={selectedSizes}
          onDownload={handleDownloadSingle}
          onRemove={handleRemoveImage}
          onNameChange={handleNameChange}
          isProcessing={isProcessing}
        />
      ))}
    </div>
  );

  // File name preview helper function
  const getFileNamePreview = (size: PrintSize) => {
    return `${baseFileName || 'untitled'}_${size.width}x${size.height}_${customDpi}dpi.${selectedFormat}`;
  };

  // Hero elementini daha erken render etmek için ref kullan
  const heroRef = useRef<HTMLDivElement>(null);

  // Sayfa yüklenme zamanını takip etmek ve LCP optimizasyonu için
  useLayoutEffect(() => {
    // Report the hero text as the LCP element as early as possible
    window.LCPReporter = (element) => {
      if (element && 'PerformanceObserver' in window) {
        try {
          // Create a mark for performance measurement
          performance.mark('hero-text-displayed');
        } catch (e) {
          // Silent error for environments without perf API
        }
      }
    };

    // Clean up
    return () => {
      delete window.LCPReporter;
    };
  }, []);

  // Modern UI için yenilenmiş SEO içeriği
  const renderSEOContent = () => (
    <section className="bg-white rounded-xl shadow-sm border p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">About Our Image DPI Converter</h2>
      <div className="prose prose-sm max-w-none text-gray-600">
        <p className="leading-relaxed">
          Our <strong>free image DPI converter</strong> lets you modify the DPI of any image quickly and easily.
          Convert common image formats like <strong>JPG, PNG, and GIF</strong> to any desired DPI setting,
          from <strong>72 DPI</strong> (standard screen resolution) to <strong>600 DPI</strong> (high quality print).
        </p>
        <p className="mt-3 leading-relaxed">
          Whether you need to adjust dots per inch for professional printing or digital display, our
          user-friendly image DPI converter makes conversion simple and efficient. This DPI changer converts images
          without losing quality, ensuring your converted images maintain their original clarity.
        </p>
        <p className="mt-3 leading-relaxed">
          Need to convert images in bulk? Our image DPI converter handles that too, saving you time by processing multiple
          files at once. Experience high quality image DPI conversions with our powerful yet simple converter.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
          <h3 className="text-sm font-medium text-blue-800 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Supported Input Formats
          </h3>
          <p className="text-xs text-blue-700 mt-2">JPEG, PNG, GIF, BMP, WEBP and more file types</p>
        </div>
        <div className="bg-green-50 p-4 rounded-xl border border-green-100">
          <h3 className="text-sm font-medium text-green-800 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            DPI Range Options
          </h3>
          <p className="text-xs text-green-700 mt-2">72, 96, 150, 300, 600 DPI and custom settings</p>
        </div>
      </div>
    </section>
  );

  const handleGuestSubscriptionSuccess = (_guestSession: any) => {
    setShowGuestSubscriptionModal(false);
    toast.success("Subscription activated successfully! Enjoy unlimited premium conversions.");
  };

  const handleCloseGuestModal = () => {
    setShowGuestSubscriptionModal(false);
  };

  const renderHomeView = () => (
    <>
      <HeroSection heading={pageMeta.heading} description={pageMeta.descriptionText} />
      <main className="max-w-5xl mx-auto px-4 py-6 md:py-8 -mt-10 md:-mt-20 relative z-10">
        <SubscriptionBanner />

        {/* Modern adım göstergesi */}
        <div className="bg-white backdrop-blur-sm rounded-xl shadow-sm border border-gray-200 mb-6 p-4 hidden sm:block">
          <div className="flex items-center justify-center space-x-3 text-sm">
            {[
              { step: 1, text: 'Upload Files' },
              { step: 2, text: 'Select Sizes' },
              { step: 3, text: 'Configure Settings' },
              { step: 4, text: 'Download' }
            ].map((item, i) => (
              <React.Fragment key={item.step}>
                <div className={`flex items-center space-x-2 ${(processedImages.length > 0 && item.step <= 2) ||
                  (selectedSizes.some(s => s.isSelected) && item.step <= 3) ||
                  (isProcessing && item.step === 4)
                  ? 'text-blue-600'
                  : 'text-gray-400'
                  }`}>
                  <span className="w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm
              ${(processedImages.length > 0 && item.step <= 2) ||
                (selectedSizes.some(s => s.isSelected) && item.step <= 3) ||
                (isProcessing && item.step === 4)
                  ? 'bg-blue-100 text-blue-600 border-2 border-blue-200'
                  : 'bg-gray-100 text-gray-500 border border-gray-200'
              }">
                    {item.step}
                  </span>
                  <span>{item.text}</span>
                </div>
                {i < 3 && (
                  <div className="w-12 h-px bg-gray-200 relative">
                    {((processedImages.length > 0 && i < 1) ||
                      (selectedSizes.some(s => s.isSelected) && i < 2) ||
                      (isProcessing && i < 3)) && (
                        <div className="absolute inset-0 bg-blue-500 transition-all duration-500"></div>
                      )}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 transition-all hover:shadow-md">
              <h2 className="text-lg font-medium text-gray-800 mb-4">Upload Files</h2>
              <Suspense fallback={<div className="animate-pulse h-32 bg-gray-100 rounded-lg"></div>}>
                <ImageUploader onImageUpload={handleImageUpload} acceptPdf={true} />
              </Suspense>
            </div>

            {processedImages.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 transition-all hover:shadow-md">
                <h2 className="text-lg font-medium text-gray-800 mb-4">Uploaded Files</h2>
                {renderImageList()}
              </div>
            )}
          </div>

          <div className="lg:col-span-3 space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 transition-all hover:shadow-md">
              <h2 className="text-lg font-medium text-gray-800 mb-4">Print Sizes</h2>
              <Suspense fallback={<div className="animate-pulse h-32 bg-gray-100 rounded-lg"></div>}>
                <SizeSelector
                  selectedSizes={selectedSizes}
                  onSizeToggle={handleSizeToggle}
                  selectedFormat={selectedFormat}
                  onFormatChange={handleFormatChange}
                />
              </Suspense>
            </div>

            <Suspense fallback={<div className="animate-pulse h-32 bg-gray-100 rounded-lg"></div>}>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 transition-all hover:shadow-md">
                <ConversionSettings
                  dpi={customDpi}
                  onDpiChange={setCustomDpi}
                  format={selectedFormat}
                  onFormatChange={handleFormatChange}
                >
                  <div className="mt-3 text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <p className="flex items-center">
                      <svg className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      DPI (dots per inch) affects print quality. Higher DPI values like 300 or 600 DPI create sharper printed images.
                    </p>
                  </div>

                  {processedImages.length > 0 && selectedSizes.some(s => s.isSelected) && (
                    <div className="mt-5 pt-4 border-t">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-600 font-medium">
                          {processedImages.length} file(s) × {selectedSizes.filter(s => s.isSelected).length} size(s)
                        </span>
                      </div>
                      <button
                        onClick={handleBulkDownload}
                        disabled={isProcessing}
                        className={`
                    w-full py-3 px-4 rounded-lg font-medium text-sm text-center
                    transition-all
                    ${isProcessing
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-sm hover:shadow'}
                  `}
                        aria-label="Process and download all files"
                      >
                        {isProcessing ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" role="presentation" aria-hidden="true">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Process & Download All
                          </span>
                        )}
                      </button>
                    </div>
                  )}
                </ConversionSettings>
              </div>
            </Suspense>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 transition-all hover:shadow-md">
              <h2 className="text-lg font-medium text-gray-800 mb-4">DPI Conversion FAQ</h2>
              <div className="space-y-3">
                <details className="group">
                  <summary className="flex items-center justify-between cursor-pointer text-sm font-medium text-gray-700 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <span>What is DPI in images?</span>
                    <svg className="h-4 w-4 text-gray-500 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="mt-2 text-sm text-gray-600 px-3">
                    DPI (dots per inch) measures print resolution. It determines how many dots of ink are printed per inch, affecting image quality.
                  </p>
                </details>

                <details className="group">
                  <summary className="flex items-center justify-between cursor-pointer text-sm font-medium text-gray-700 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <span>Why convert image DPI?</span>
                    <svg className="h-4 w-4 text-gray-500 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="mt-2 text-sm text-gray-600 px-3">
                    Higher DPI (like 300-600) is ideal for printing, while lower DPI (72-96) is suitable for digital display and web use.
                  </p>
                </details>

                <details className="group">
                  <summary className="flex items-center justify-between cursor-pointer text-sm font-medium text-gray-700 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <span>Will changing DPI affect image quality?</span>
                    <svg className="h-4 w-4 text-gray-500 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="mt-2 text-sm text-gray-600 px-3">
                    Our DPI converter preserves image quality while adjusting resolution. For best results when increasing DPI, start with high-resolution images.
                  </p>
                </details>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 transition-all hover:shadow-md hover:border-blue-200">
              <div className="rounded-full bg-blue-50 h-12 w-12 flex items-center justify-center text-blue-600 mb-4">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="font-medium text-gray-800 mb-2">Why Use Our Image DPI Converter</h3>
              <p className="text-sm text-gray-600">Our tool makes it easy to convert images to the right DPI for any purpose, whether it's printing, publishing, or digital display.</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 transition-all hover:shadow-md hover:border-blue-200">
              <div className="rounded-full bg-blue-50 h-12 w-12 flex items-center justify-center text-blue-600 mb-4">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-medium text-gray-800 mb-2">Supported Platforms</h3>
              <p className="text-sm text-gray-600">Works on Windows, macOS, Linux, iOS and Android - no software installation required.</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 transition-all hover:shadow-md hover:border-blue-200">
              <div className="rounded-full bg-blue-50 h-12 w-12 flex items-center justify-center text-blue-600 mb-4">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="font-medium text-gray-800 mb-2">Security & Privacy</h3>
              <p className="text-sm text-gray-600">All image processing happens in your browser. Your files are never uploaded to our servers.</p>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-gray-200">
          {landingPage && <LandingContent page={landingPage} />}
          {location.pathname === '/' && (
            <LandingContent page={{ path: '/', title: '', description: '', heading: '', highlight: '', intro: '', ...homePageData }} />
          )}
          {renderSEOContent()}
        </div>
      </main>
    </>
  );

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <div className="p-8 text-center">
          <h3 className="text-lg font-medium text-red-600">Something went wrong</h3>
          <p className="mt-2 text-gray-600">{error.message}</p>
        </div>
      )}
    >
      <HelmetProvider>
        <div className="min-h-screen bg-white">
          <Helmet>
            <title>{pageMeta.title}</title>
            <meta name="description" content={pageMeta.description} />
            {/* Deprecated keywords meta removed for clean 2026 SEO standard */}
            {/* Image DPI Converter için ek meta etiketleri */}
            <meta property="og:title" content={pageMeta.title} />
            <meta property="og:description" content={pageMeta.description} />
            <meta property="og:url" content={`https://www.imagedpiconverter.com${location.pathname === '/' ? '' : location.pathname}`} />
            <meta property="og:type" content="website" />
            <meta property="og:image" content="https://www.imagedpiconverter.com/og-preview.png" />
            <link rel="canonical" href={`https://www.imagedpiconverter.com${location.pathname === '/' ? '' : location.pathname}`} />
            {pageMeta.schema && (
              <script type="application/ld+json">
                {JSON.stringify(pageMeta.schema)}
              </script>
            )}
            {['/', '/convert-to-300-dpi', '/convert-to-600-dpi', '/bulk-dpi-converter', '/dpi-calculator'].includes(location.pathname) && (
              <script type="application/ld+json">
                {JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": [
                    {
                      "@type": "Question",
                      "name": "What is DPI in images?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "DPI (dots per inch) measures print resolution. It determines how many dots of ink are printed per inch, which affects image sharpness and quality when printed on physical materials."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Why convert image DPI?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Converting your image's DPI to 300 DPI or 600 DPI is required for high-quality printing, as standard printers need high-density pixels to prevent blurriness. Digital displays generally use 72 DPI to 96 DPI."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Will changing DPI affect image quality?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Our online DPI converter adjusts the resolution metadata of your image without stretching or losing quality. For best results when scaling up, always use high-resolution source images."
                      }
                    }
                  ]
                })}
              </script>
            )}
          </Helmet>

          <ScrollToTop />

          <Header />
          <Routes>
            <Route path="/pricing" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <PricingPage />
              </Suspense>
            } />
            <Route path="/privacy" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <PrivacyPolicy />
              </Suspense>
            } />
            <Route path="/terms" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <TermsOfService />
              </Suspense>
            } />
            <Route path="/refund" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <RefundPolicy />
              </Suspense>
            } />
            <Route path="/about" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <About />
              </Suspense>
            } />
            <Route path="/contact" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <Contact />
              </Suspense>
            } />
            <Route path="/tools" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <ToolsIndex />
              </Suspense>
            } />
            <Route path="/dpi-checker" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <DpiChecker />
              </Suspense>
            } />
            <Route path="/print-size-checker" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <PrintSizeChecker />
              </Suspense>
            } />
            <Route path="/pixels-to-inches" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <PixelCalculator />
              </Suspense>
            } />
            <Route path="/image-resizer" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <ImageResizer />
              </Suspense>
            } />
            <Route path="/crop-to-print-size" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <CropToPrintSize />
              </Suspense>
            } />
            <Route path="/printable-size-pack" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <PrintableSizePack />
              </Suspense>
            } />
            <Route path="/bleed-tool" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <BleedTool />
              </Suspense>
            } />
            <Route path="/kdp-cover-calculator" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <KdpCoverCalculator />
              </Suspense>
            } />
            <Route path="/print-size-chart" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <PrintSizeChart />
              </Suspense>
            } />
            <Route path="/auth/confirm" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <AuthConfirm />
              </Suspense>
            } />
            <Route path="/thank-you" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <ThankYouPage />
              </Suspense>
            } />
            <Route path="/login" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <Login />
              </Suspense>
            } />
            <Route path="/signup" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <Login />
              </Suspense>
            } />
            <Route path="/dashboard" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <Dashboard />
              </Suspense>
            } />
            <Route path="/credit-history" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <CreditHistoryPage />
              </Suspense>
            } />
            <Route path="/admin" element={
              <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <AdminPanel />
              </Suspense>
            } />
            <Route index element={renderHomeView()} />
            <Route path="/convert-to-300-dpi" element={renderHomeView()} />
            <Route path="/convert-to-600-dpi" element={renderHomeView()} />
            <Route path="/bulk-dpi-converter" element={renderHomeView()} />
            <Route path="/dpi-calculator" element={renderHomeView()} />
            {LANDING_PAGES.map(p => (
              <Route key={p.path} path={p.path} element={renderHomeView()} />
            ))}
            <Route path="*" element={
              <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4 text-center">
                <h1 className="text-4xl font-extrabold text-gray-900 mb-2">404 - Page Not Found</h1>
                <p className="text-gray-600 mb-6">The page you are looking for does not exist or has been moved.</p>
                <Link to="/" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-lg transition-colors shadow-sm">
                  Go back to Home
                </Link>
              </div>
            } />
          </Routes>

          <footer className="bg-gradient-to-b from-slate-50 to-white border-t border-gray-200 mt-16 py-12 md:py-16" style={{ position: 'relative', zIndex: 20 }}>
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Image DPI Converter Online</h2>
                  <p className="text-gray-600 mb-6">
                    Our free image DPI converter helps you convert JPG, PNG, GIF and other image formats to your desired DPI with a user-friendly interface.
                  </p>
                  
                </div>

                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Features</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-center">
                      <svg className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Convert any image format
                    </li>
                    <li className="flex items-center">
                      <svg className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      High quality DPI conversion
                    </li>
                    <li className="flex items-center">
                      <svg className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Modify DPI from 72 to 600
                    </li>
                    <li className="flex items-center">
                      <svg className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Bulk image processing
                    </li>
                    <li className="flex items-center">
                      <svg className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      User-friendly interface
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Common Uses</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-center">
                      <svg className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Preparing images for print
                    </li>
                    <li className="flex items-center">
                      <svg className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Converting web images to print quality
                    </li>
                    <li className="flex items-center">
                      <svg className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Reducing file size for web
                    </li>
                    <li className="flex items-center">
                      <svg className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Meeting publishing requirements
                    </li>
                    <li className="flex items-center">
                      <svg className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Professional photography work
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-wrap justify-center gap-4 mt-8 pt-6 border-t border-gray-200">
                <Link to="/dpi-checker" className="px-3 py-1 text-sm text-gray-700 hover:text-blue-600 hover:underline">
                  DPI Checker
                </Link>
                <Link to="/print-size-checker" className="px-3 py-1 text-sm text-gray-700 hover:text-blue-600 hover:underline">
                  Print Size Checker
                </Link>
                <Link to="/pixels-to-inches" className="px-3 py-1 text-sm text-gray-700 hover:text-blue-600 hover:underline">
                  Pixels to Inches
                </Link>
                <Link to="/image-resizer" className="px-3 py-1 text-sm text-gray-700 hover:text-blue-600 hover:underline">
                  Image Resizer
                </Link>
                <Link to="/about" className="px-3 py-1 text-sm text-gray-700 hover:text-blue-600 hover:underline">
                  About Us
                </Link>
                <Link to="/pricing" className="px-3 py-1 text-sm text-gray-700 hover:text-blue-600 hover:underline">
                  Pricing
                </Link>
                <Link to="/contact" className="px-3 py-1 text-sm text-gray-700 hover:text-blue-600 hover:underline">
                  Contact
                </Link>
                <Link to="/privacy" className="px-3 py-1 text-sm text-gray-700 hover:text-blue-600 hover:underline">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="px-3 py-1 text-sm text-gray-700 hover:text-blue-600 hover:underline">
                  Terms of Service
                </Link>
                <Link to="/refund" className="px-3 py-1 text-sm text-gray-700 hover:text-blue-600 hover:underline">
                  Refund Policy
                </Link>
              </div>

              <div className="border-t border-gray-200 mt-4 pt-6 text-center text-sm text-gray-500">
                © {new Date().getFullYear()} Free Image DPI Converter - Convert images with precision
              </div>
            </div>
          </footer>

          <Toaster
            position="bottom-right"
            toastOptions={{
              className: 'rounded-xl shadow-lg border border-gray-100',
              duration: 3000,
            }}
          />

          {/* Guest Subscription Modal */}
          <GuestSubscriptionModal
            isOpen={showGuestSubscriptionModal}
            onClose={handleCloseGuestModal}
            onSubscriptionSuccess={handleGuestSubscriptionSuccess}
          />


        </div>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;